/**
 * HealthIntelligenceCard - Home screen insight card
 * 
 * Shows ONE primary nutrition insight with "Fix This" + "Ask Why" actions.
 * Gated by tier/trial: Free gets limited suggestions + upsell.
 */

import React, { useMemo } from 'react';
import { Sparkles, Zap, MessageCircle, Lock } from 'lucide-react';
import { brand } from '../../styles/brand';
import {
  generateInsight,
  getInsightIcon,
  getFixThisAccess,
  getAskWhyLimit,
  INSIGHT_TYPES
} from '../../utils/healthInsights';

const { colors, gradients, radius, spacing, typography } = brand;

const HealthIntelligenceCard = ({
  // Data
  calories = 0,
  calorieGoal = 2000,
  protein = 0,
  proteinGoal = 150,
  fat = 0,
  fatGoal = 65,
  carbs = 0,
  carbsGoal = 250,
  // Tier/access
  userTier = 'free',
  isTrial = false,
  // Actions
  onFixThis,
  onAskWhy,
  onUpgradeGenius,
  // Ask Why usage
  askWhyUsedToday = 0
}) => {
  const insight = useMemo(() => generateInsight({
    calories, calorieGoal, protein, proteinGoal, fat, fatGoal, carbs, carbsGoal
  }), [calories, calorieGoal, protein, proteinGoal, fat, fatGoal, carbs, carbsGoal]);

  const fixAccess = getFixThisAccess(userTier, isTrial);
  const askWhyLimit = getAskWhyLimit(userTier, isTrial);
  const askWhyRemaining = askWhyLimit === Infinity ? Infinity : Math.max(0, askWhyLimit - askWhyUsedToday);
  const isOnTrack = insight.type === INSIGHT_TYPES.ON_TRACK;
  const isNoData = insight.type === INSIGHT_TYPES.NO_DATA;
  const isWarning = !isOnTrack && !isNoData;

  const handleFixThis = () => {
    if (isNoData) return;
    // Always open FixThis modal - it handles tier gating internally
    onFixThis?.(insight.type, insight.headline);
  };

  const handleAskWhy = () => {
    if (isNoData) return;

    if (askWhyRemaining <= 0) {
      onUpgradeGenius?.();
      return;
    }
    // Open Smart Coach in clean mode (no prefill, shows suggestion chips)
    onAskWhy?.('', 'smart_coach');
  };

  return (
    <div
      data-testid="health-intelligence-card"
      style={{
        marginBottom: spacing.s2,
        borderRadius: radius.lg,
        border: isWarning
          ? '1px solid rgba(255, 183, 77, 0.3)'
          : isOnTrack
            ? '1px solid rgba(56, 239, 125, 0.25)'
            : '1px solid rgba(102, 126, 234, 0.2)',
        background: isWarning
          ? 'linear-gradient(135deg, rgba(255, 183, 77, 0.08) 0%, rgba(255, 152, 0, 0.04) 100%)'
          : isOnTrack
            ? 'linear-gradient(135deg, rgba(56, 239, 125, 0.08) 0%, rgba(17, 153, 142, 0.04) 100%)'
            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.04) 100%)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{ padding: `${spacing.s3} ${spacing.s4} 0` }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: spacing.s1
        }}>
          <Sparkles size={14} style={{ color: '#667eea' }} />
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: '#667eea',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Health Intelligence
          </span>
          <span style={{
            fontSize: '10px',
            color: colors.textSecondary,
            fontWeight: '500',
            marginLeft: '6px'
          }}>
            Nutrition &middot; Supplements &middot; Peptides
          </span>
        </div>

        {/* Insight */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: spacing.s2
        }}>
          <span style={{ fontSize: '18px', lineHeight: '1.2' }}>
            {getInsightIcon(insight.type)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '15px',
              fontWeight: '700',
              color: colors.text,
              lineHeight: '1.3',
              marginBottom: '2px'
            }}>
              {insight.headline}
            </div>
            {insight.reason && (
              <div style={{
                fontSize: '12px',
                color: colors.textSecondary,
                lineHeight: '1.4'
              }}>
                {insight.reason}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {!isNoData && (
        <div style={{
          display: 'flex',
          gap: spacing.s2,
          padding: `0 ${spacing.s4} ${spacing.s3}`
        }}>
          {/* Fix This */}
          <button
            data-testid="health-intel-fix-this"
            onClick={handleFixThis}
            style={{
              flex: 1,
              padding: `${spacing.s2} ${spacing.s3}`,
              background: isOnTrack
                ? 'rgba(56, 239, 125, 0.12)'
                : 'rgba(255, 183, 77, 0.12)',
              border: isOnTrack
                ? '1px solid rgba(56, 239, 125, 0.3)'
                : '1px solid rgba(255, 183, 77, 0.3)',
              borderRadius: radius.md,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            <Zap size={14} style={{ color: isOnTrack ? '#38ef7d' : '#ffb74d' }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: isOnTrack ? '#38ef7d' : '#ffb74d'
            }}>
              {isOnTrack ? 'Stay Sharp' : 'Fix This'}
            </span>
            {fixAccess === 'upsell' && (
              <Lock size={10} style={{ color: '#ffb74d', opacity: 0.6 }} />
            )}
          </button>

          {/* Ask Why */}
          <button
            data-testid="health-intel-ask-why"
            onClick={handleAskWhy}
            style={{
              flex: 1,
              padding: `${spacing.s2} ${spacing.s3}`,
              background: 'rgba(102, 126, 234, 0.12)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: radius.md,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            <MessageCircle size={14} style={{ color: '#667eea' }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#667eea'
            }}>
              Ask Coach
            </span>
            {askWhyRemaining !== Infinity && (
              <span style={{
                fontSize: '10px',
                color: askWhyRemaining > 0 ? 'rgba(102, 126, 234, 0.6)' : '#f44336',
                fontWeight: '500'
              }}>
                {askWhyRemaining > 0 ? `${askWhyRemaining}` : '0'}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default HealthIntelligenceCard;
